import { createTheme, Theme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import {
  AppPalette,
  AppRole,
  darkPalette,
  fontFamily,
  lightPalette,
  radii,
  roleAccents,
} from './tokens';

/**
 * Expose our semantic design tokens on the MUI theme via module augmentation,
 * so any component can read them through `theme.app.*` while still benefiting
 * from MUI's own palette for built-in components.
 */
declare module '@mui/material/styles' {
  interface Theme {
    app: AppPalette;
  }
  interface ThemeOptions {
    app?: AppPalette;
  }
}

export function createAppTheme(mode: PaletteMode, role: AppRole = 'parent'): Theme {
  const base = mode === 'light' ? lightPalette : darkPalette;
  const app: AppPalette = { ...base, ...roleAccents[role][mode] };

  return createTheme({
    app,
    palette: {
      mode,
      primary: {
        main: app.primary,
        contrastText: app.primaryForeground,
      },
      secondary: {
        main: app.secondary,
        contrastText: app.secondaryForeground,
      },
      error: {
        main: app.destructive,
        contrastText: app.destructiveForeground,
      },
      info: {
        main: app.link,
      },
      background: {
        default: app.background,
        paper: app.card,
      },
      text: {
        primary: app.foreground,
        secondary: app.mutedForeground,
      },
      divider: app.border,
    },
    shape: {
      borderRadius: radii.lg,
    },
    typography: {
      fontFamily,
      // heading 2 — used for the login headline
      h2: {
        fontSize: 30,
        lineHeight: '36px',
        fontWeight: 600,
        letterSpacing: '-0.5px',
      },
      // heading 3
      h3: {
        fontSize: 24,
        lineHeight: '28px',
        fontWeight: 600,
        letterSpacing: '-0.5px',
      },
      // heading 4
      h4: {
        fontSize: 20,
        lineHeight: '24px',
        fontWeight: 600,
      },
      // paragraph / regular
      body1: {
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 400,
      },
      // paragraph / small
      body2: {
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 400,
      },
      // paragraph / mini
      caption: {
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
      },
      button: {
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 500,
        textTransform: 'none',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: app.background,
            color: app.foreground,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: radii.full,
            minHeight: 40,
            paddingInline: 24,
          },
        },
      },
    },
  });
}
